import { useParams } from "@tanstack/solid-router";
import { Show, createMemo } from "solid-js";

import { AppShell } from "~/components/AppShell";
import { PartnerAISettingsPanel } from "~/components/PartnerAISettingsPanel";
import { useMyWorkspaces } from "~/lib/hooks";

/**
 * Partner-admin landing surface, scoped through the current workspace.
 *
 * Authority is per (user, partner). The page reads `/v1/me/workspaces`
 * to find the current workspace's `partner_id` + confirm the caller has
 * `is_partner_admin=true` on that partner. If not, renders a friendly
 * forbidden card rather than relying solely on the API's 403 — the
 * AppShell nav link is gated separately so this should only ever fire
 * if someone deep-links here.
 */
export function PartnerAdminPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string };
  const myWorkspaces = useMyWorkspaces();

  const currentMembership = createMemo(() => {
    return myWorkspaces.data?.items.find((m) => m.workspace_key === params().wsKey);
  });

  const partnerId = createMemo(() => currentMembership()?.partner_id ?? "");
  const isPartnerAdmin = createMemo(() => currentMembership()?.is_partner_admin === true);

  return (
    <AppShell wsKey={params().wsKey} pageTitle="Partner admin">
      <Show when={myWorkspaces.isLoading}>
        <p class="text-sm opacity-60">Loading…</p>
      </Show>

      <Show when={myWorkspaces.data && !currentMembership()}>
        <div role="alert" class="alert alert-error">
          You don't have access to this workspace.
        </div>
      </Show>

      <Show when={currentMembership() && !isPartnerAdmin()}>
        <div role="alert" class="alert alert-warning">
          <span>
            You're not a partner-admin on this workspace's partner. Ask an existing partner-admin to
            grant you access via the
            <code class="mx-1">scaicrm partner-admin grant</code>
            CLI.
          </span>
        </div>
      </Show>

      <Show when={isPartnerAdmin() && partnerId()}>
        <div class="flex flex-col gap-4">
          <div class="card bg-base-100 shadow-sm">
            <div class="card-body">
              <h3 class="card-title text-base">Partner</h3>
              <p class="text-sm">
                Managing <strong>{currentMembership()!.tenant_name}</strong>'s parent partner.
              </p>
              <p class="text-xs opacity-60">
                Partner id: <code>{partnerId()}</code>
              </p>
            </div>
          </div>

          <PartnerAISettingsPanel partnerId={partnerId} catalogProbeWsKey={() => params().wsKey} />
        </div>
      </Show>
    </AppShell>
  );
}
